import { Controller } from "@hotwired/stimulus"

// Generic controller for ajax tabs - works for both claims and bills
// Connects to data-controller="ajax-tabs"
export default class extends Controller {
  static targets = ["tab", "content", "tabContent"]
  static values = {
    updateTabUrl: String
  }

  initialize() {
    this.boundHandlePopstate = this.handlePopstate.bind(this)
  }

  connect() {
    window.addEventListener('popstate', this.boundHandlePopstate)
    this.updateActiveTab()
  }

  navigate(event) {
    event.preventDefault()

    const clickedTab = event.currentTarget
    const targetUrl = clickedTab.getAttribute('href')
    const tabName = clickedTab.getAttribute('data-tab-name')

    // Update URL without page reload
    window.history.pushState(null, null, targetUrl)

    // Update active tab appearance
    this.setActiveTab(clickedTab)

    // Show loading animation for the clicked tab
    this.showLoadingForTab(clickedTab)

    // Load content via Turbo Stream
    this.loadTabContent(tabName)
  }

  setActiveTab(activeTab) {
    // Remove active styling from all tabs
    this.tabTargets.forEach(tab => {
      const tabDiv = tab.querySelector('div')
      tabDiv.classList.remove('border-b-2', 'border-purple', 'mb-[-2px]')
      tab.classList.remove('mb-[-2px]')
    })

    // Add active styling to clicked tab
    const activeTabDiv = activeTab.querySelector('div')
    activeTabDiv.classList.add('border-b-2', 'border-purple', 'mb-[-2px]')
    activeTab.classList.add('mb-[-2px]')
  }

  updateActiveTab() {
    // Find and highlight the tab that matches current URL
    const currentPath = window.location.pathname
    this.tabTargets.forEach(tab => {
      if (tab.getAttribute('href') === currentPath) {
        this.setActiveTab(tab)
      }
    })
  }

  async loadTabContent(tabName) {
    try {
      // Use the update_tab URL provided as a data attribute
      const updateTabUrl = this.updateTabUrlValue

      // Use Turbo to submit the request
      const formData = new FormData()
      formData.append('tab', tabName)

      const response = await fetch(updateTabUrl, {
        method: 'POST',
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: formData
      })

      if (response.ok) {
        const streamMessage = await response.text()
        // Use Turbo to process the stream response
        Turbo.renderStreamMessage(streamMessage)
      } else {
        console.error('Failed to load tab content:', response.statusText)
      }
    } catch (error) {
      console.error('Error loading tab content:', error)
    } finally {
      // Hide loading animation for all tabs after request completes
      this.hideAllLoading()
    }
  }

  // Show loading animation for a specific tab
  showLoadingForTab(tab) {
    const tabContent = this.findTabContentForTab(tab)
    if (tabContent) {
      // Add loading animation class - keeps existing border-b-2
      tabContent.classList.add('tab-loading')
    }
  }

  // Hide loading animation for a specific tab
  hideLoadingForTab(tab) {
    const tabContent = this.findTabContentForTab(tab)
    if (tabContent) {
      tabContent.classList.remove('tab-loading')
    }
  }

  // Hide all loading animations
  hideAllLoading() {
    this.tabContentTargets.forEach(tabContent => {
      tabContent.classList.remove('tab-loading')
    })
  }

  // Find the tab content element for a given tab
  findTabContentForTab(tab) {
    return tab.querySelector('[data-ajax-tabs-target="tabContent"]')
  }

  // Handle browser back/forward buttons
  handlePopstate(event) {
    // Only handle popstate if we still have our targets
    if (this.hasContentTarget && this.hasTabTarget) {
      this.updateActiveTab()
      // Find the tab name for the current URL
      const currentPath = window.location.pathname
      const activeTab = this.tabTargets.find(tab => tab.getAttribute('href') === currentPath)
      if (activeTab) {
        const tabName = activeTab.getAttribute('data-tab-name')
        // Show loading animation for the active tab during popstate navigation
        this.showLoadingForTab(activeTab)
        this.loadTabContent(tabName)
      }
    }
  }

  disconnect() {
    window.removeEventListener('popstate', this.boundHandlePopstate)
  }
}
